import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatApiErrorDetail } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { PRICES } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Plus, Trash2, TrendingUp, History, Users as UsersIcon, ArrowUpRight, Pencil } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

function eur(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('fi-FI', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function dateFi(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fi-FI');
}

export default function Prices() {
  const { user } = useAuth();
  const isAdmin = user && user.role === 'admin';
  // `groups` = lista hintaryhmistä (price_groups-kokoelmasta) sort_order-mukaan.
  // `data` = hintahistoria-yhteenveto ryhmäavaimittain (current_price + history).
  const [groups, setGroups] = useState([]);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [dialogGroup, setDialogGroup] = useState(null);
  const [form, setForm] = useState({ effective_from: format(new Date(), 'yyyy-MM-dd'), unit_price: '' });
  const [saving, setSaving] = useState(false);
  // Lisää uusi hintaryhmä -dialog
  const [addGroupOpen, setAddGroupOpen] = useState(false);
  const [newGroup, setNewGroup] = useState({ key: '', name: '', sort_order: 100 });
  // Muokkaa olemassa olevan ryhmän nimi
  const [editGroup, setEditGroup] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', sort_order: 100 });

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: g }, { data: p }] = await Promise.all([
        api.get('/price-groups'),
        api.get('/prices'),
      ]);
      setGroups(g);
      setData(p);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Hintojen lataus epäonnistui');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openAdd = (groupKey) => {
    setDialogGroup(groupKey);
    setForm({ effective_from: format(new Date(), 'yyyy-MM-dd'), unit_price: '' });
  };

  const save = async () => {
    if (!form.effective_from || !form.unit_price) {
      toast.error('Täytä päivämäärä ja hinta');
      return;
    }
    setSaving(true);
    try {
      await api.post('/prices', {
        group: dialogGroup,
        effective_from: form.effective_from,
        unit_price: Number(String(form.unit_price).replace(',', '.')),
      });
      toast.success('Hinta tallennettu');
      setDialogGroup(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Tallennus epäonnistui');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (entry) => {
    if (!confirm(`Poista hinta ${eur(entry.unit_price)} (voimassa ${dateFi(entry.effective_from)})?`)) return;
    try {
      await api.delete(`/prices/${entry.id}`);
      toast.success('Hinta poistettu');
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Poisto epäonnistui');
    }
  };

  const saveNewGroup = async () => {
    const k = (newGroup.key || '').trim();
    const n = (newGroup.name || '').trim();
    if (!k || !n) {
      toast.error('Anna sekä tunnus että nimi');
      return;
    }
    try {
      await api.post('/price-groups', {
        key: k,
        name: n,
        sort_order: Number(newGroup.sort_order) || 100,
        active: true,
      });
      toast.success(`Hintaryhmä "${n}" lisätty`);
      setAddGroupOpen(false);
      setNewGroup({ key: '', name: '', sort_order: 100 });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Lisäys epäonnistui');
    }
  };

  const openEditGroup = (grp) => {
    setEditGroup(grp);
    setEditForm({ name: grp.name, sort_order: grp.sort_order });
  };

  const saveEditGroup = async () => {
    const n = (editForm.name || '').trim();
    if (!n) {
      toast.error('Nimi ei voi olla tyhjä');
      return;
    }
    try {
      await api.patch(`/price-groups/${editGroup.id}`, {
        name: n,
        sort_order: Number(editForm.sort_order) || 100,
      });
      toast.success('Ryhmä päivitetty');
      setEditGroup(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Päivitys epäonnistui');
    }
  };

  const removeGroup = async (grp) => {
    const customers = data[grp.key]?.customer_count || 0;
    if (customers > 0) {
      toast.error(`Ryhmässä on vielä ${customers} asiakasta. Siirrä asiakkaat muuhun ryhmään ensin.`);
      return;
    }
    if (!confirm(`Poistetaanko hintaryhmä "${grp.name}" (${grp.key})? Kaikki ryhmän hintahistoria poistetaan myös.`)) return;
    try {
      await api.delete(`/price-groups/${grp.id}`);
      toast.success(`Ryhmä "${grp.name}" poistettu`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Poisto epäonnistui');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.15em] uppercase text-[#6A6D66] font-semibold">Hinnasto</p>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-[#1C1C1A]">Hinnat</h1>
          <p className="text-sm text-[#6A6D66] mt-1 max-w-2xl">
            Päiväkohtainen yksikköhinta hintaryhmittäin. Hinta on voimassa antamastasi päivästä alkaen, kunnes ryhmälle lisätään uusi hinta uudemmalla päivämäärällä.
            Lähetteet ja laskut käyttävät <strong>toimituspäivän</strong> mukaan voimassa ollutta hintaa.
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => setAddGroupOpen(true)}
            data-testid="prices-add-group-button"
            className="h-9 rounded-full bg-[#2A4B30] hover:bg-[#1E3622] text-white shrink-0"
          >
            <Plus size={16} className="mr-1" />
            Lisää hintaryhmä
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {groups.map((grp) => {
          const g = grp.key;
          const summary = data[g] || {};
          const history = summary.history || [];
          return (
            <div
              key={grp.id}
              data-testid={`${PRICES.groupCardPrefix}${g}`}
              className="bg-white border border-[#E5E6E3] rounded-2xl shadow-sm overflow-hidden"
            >
              <div className="px-5 py-4 bg-gradient-to-br from-[#F4F6F3] to-white border-b border-[#E5E6E3]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#6A6D66] font-semibold">Hintaryhmä</p>
                    <h2 className="font-heading text-xl text-[#1C1C1A] mt-0.5">
                      {g} {grp.name}
                    </h2>
                  </div>
                  <div className="flex items-center gap-1">
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => openEditGroup(grp)}
                        data-testid={`prices-edit-group-${g}`}
                        title="Muokkaa ryhmän nimeä"
                        className="w-7 h-7 rounded-full hover:bg-white text-[#6A6D66] hover:text-[#2A4B30] flex items-center justify-center transition-colors"
                      >
                        <Pencil size={13} />
                      </button>
                    )}
                    {isAdmin && (summary.customer_count || 0) === 0 && (
                      <button
                        type="button"
                        onClick={() => removeGroup(grp)}
                        data-testid={`prices-delete-group-${g}`}
                        title="Poista hintaryhmä (vain jos ei asiakkaita)"
                        className="w-7 h-7 rounded-full hover:bg-[#FBEAE5] text-[#A0A39C] hover:text-[#C05A3C] flex items-center justify-center transition-colors"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                    <div className="bg-[#2A4B30] text-white px-3 py-1.5 rounded-full text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5 ml-1">
                      <TrendingUp size={12} />
                      Voimassa
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="font-heading text-4xl text-[#1C1C1A] tabular-nums">
                    {eur(summary.current_price)}
                  </span>
                  <span className="text-xs text-[#6A6D66]">/ kg</span>
                </div>
                <Link
                  to={`/asiakkaat?price_group=${g}`}
                  data-testid={`prices-customers-link-${g}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[#2A4B30] hover:text-[#1E3622] group/link"
                  title="Näytä tämän ryhmän asiakkaat"
                >
                  <UsersIcon size={13} />
                  <span>
                    <span className="tabular-nums font-semibold">{summary.customer_active_count ?? 0}</span>
                    {' '}
                    aktiivista asiakasta
                    {summary.customer_count > (summary.customer_active_count ?? 0) && (
                      <span className="text-[#6A6D66] font-normal"> · {summary.customer_count - summary.customer_active_count} ei-aktiivinen</span>
                    )}
                  </span>
                  <ArrowUpRight size={12} className="opacity-50 group-hover/link:opacity-100 group-hover/link:translate-x-0.5 transition-all" />
                </Link>
              </div>

              <div className="px-5 py-3 flex items-center justify-between border-b border-[#E5E6E3]">
                <div className="flex items-center gap-1.5 text-[#6A6D66] text-xs">
                  <History size={13} />
                  Hintahistoria ({history.length})
                </div>
                {isAdmin && (
                  <Button
                    onClick={() => openAdd(g)}
                    data-testid={`${PRICES.addButtonPrefix}${g}`}
                    size="sm"
                    variant="outline"
                    className="h-7 rounded-full text-xs border-[#E5E6E3]"
                  >
                    <Plus size={13} className="mr-1" />
                    Lisää
                  </Button>
                )}
              </div>

              <div className="max-h-80 overflow-y-auto">
                {history.length === 0 ? (
                  <div className="px-5 py-8 text-center text-sm text-[#6A6D66]">Ei hintoja vielä</div>
                ) : (
                  history.map((e, idx) => (
                    <div
                      key={e.id}
                      data-testid={`${PRICES.historyRowPrefix}${e.id}`}
                      className={`px-5 py-2.5 flex items-center justify-between border-b border-[#F4F4F2] hover:bg-[#FAFAF8] transition-colors ${idx === 0 ? 'bg-[#F8FAF8]' : ''}`}
                    >
                      <div>
                        <div className="text-sm font-mono font-semibold tabular-nums text-[#1C1C1A]">
                          {eur(e.unit_price)}
                        </div>
                        <div className="text-[11px] text-[#6A6D66] mt-0.5">
                          Alk. {dateFi(e.effective_from)}
                          {e.created_by && (
                            <span className="text-[#A0A39C]"> · {e.created_by}</span>
                          )}
                        </div>
                      </div>
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => remove(e)}
                          data-testid={`${PRICES.deletePrefix}${e.id}`}
                          title="Poista hintamerkintä"
                          className="w-7 h-7 rounded-full hover:bg-[#FBEAE5] text-[#A0A39C] hover:text-[#C05A3C] flex items-center justify-center transition-colors"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {loading && <div className="text-sm text-[#6A6D66]">Ladataan…</div>}

      <Dialog open={!!dialogGroup} onOpenChange={(o) => !o && setDialogGroup(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Lisää hinta · {dialogGroup && groups.find((g) => g.key === dialogGroup)?.name}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#6A6D66]">
              Hinta tulee voimaan antamastasi päivästä alkaen ja jatkuu kunnes lisäät uudemman hinnan.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Voimassa alkaen</Label>
              <Input
                type="date"
                value={form.effective_from}
                onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                data-testid={PRICES.dialogDate}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Yksikköhinta (€ / kg)</Label>
              <Input
                type="number"
                step="0.01"
                inputMode="decimal"
                value={form.unit_price}
                onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
                placeholder="esim. 9,00"
                data-testid={PRICES.dialogPrice}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogGroup(null)} disabled={saving}>Peruuta</Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid={PRICES.dialogSave}
              className="bg-[#2A4B30] hover:bg-[#1E3622] text-white"
            >
              {saving ? 'Tallennetaan…' : 'Tallenna'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lisää uusi hintaryhmä */}
      <Dialog open={addGroupOpen} onOpenChange={setAddGroupOpen}>
        <DialogContent className="max-w-md" data-testid="prices-add-group-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Lisää hintaryhmä</DialogTitle>
            <DialogDescription className="text-sm text-[#6A6D66]">
              Anna uudelle hintaryhmälle uniikki tunnus (esim. <code>4</code> tai <code>premium</code>) ja näkyvä nimi (esim. &quot;Erikoiserä&quot;). Hinnat lisätään ryhmän korttiin sen luonnin jälkeen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Tunnus (key)</Label>
              <Input
                value={newGroup.key}
                onChange={(e) => setNewGroup({ ...newGroup, key: e.target.value })}
                placeholder="4"
                data-testid="prices-add-group-key"
                autoFocus
              />
              <p className="text-[10px] text-[#6A6D66]">Käytetään asiakkaan tietueessa (`price_group`-kenttä). Lyhyt, ei välilyöntejä.</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Nimi</Label>
              <Input
                value={newGroup.name}
                onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                placeholder="Esim. Erikoiserä"
                data-testid="prices-add-group-name"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Järjestys (sort_order)</Label>
              <Input
                type="number"
                value={newGroup.sort_order}
                onChange={(e) => setNewGroup({ ...newGroup, sort_order: e.target.value })}
                data-testid="prices-add-group-sort"
              />
              <p className="text-[10px] text-[#6A6D66]">Pienempi luku näkyy ensin. 10 = Kaupat, 20 = Tukku, 30 = Muu.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddGroupOpen(false)}>Peruuta</Button>
            <Button
              onClick={saveNewGroup}
              data-testid="prices-add-group-save"
              className="bg-[#2A4B30] hover:bg-[#1E3622] text-white"
            >
              Tallenna ryhmä
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Muokkaa olemassa olevaa ryhmää */}
      <Dialog open={!!editGroup} onOpenChange={(o) => !o && setEditGroup(null)}>
        <DialogContent className="max-w-md" data-testid="prices-edit-group-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Muokkaa ryhmää · {editGroup?.key}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#6A6D66]">
              Tunnus (key) ei ole muutettavissa — se sitoo asiakkaat ja hintahistorian yhteen.
              Asiakkaiden laskutus käyttää aina <strong>nykyistä</strong> ryhmän hintaa ko. päivälle.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Nimi</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                data-testid="prices-edit-group-name"
                autoFocus
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Järjestys</Label>
              <Input
                type="number"
                value={editForm.sort_order}
                onChange={(e) => setEditForm({ ...editForm, sort_order: e.target.value })}
                data-testid="prices-edit-group-sort"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditGroup(null)}>Peruuta</Button>
            <Button
              onClick={saveEditGroup}
              data-testid="prices-edit-group-save"
              className="bg-[#2A4B30] hover:bg-[#1E3622] text-white"
            >
              Tallenna
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
